/**
 * First touch: where a person first came from, captured on the public pages and carried through
 * sign-in so the new account can be credited to search, an AI answer, a listing or a referral.
 *
 * Only the referrer's host and the landing path are kept: never the full referrer URL or its
 * query string. The first touch is never overwritten; the latest touch is kept separately.
 *
 * `captureTouch` is also inlined, as source text, into every static page, so it must stay
 * self-contained: no imports, no closures over module scope.
 */
export type TouchSource = "organic_search" | "ai_answer" | "listing" | "referral" | "direct" | "other";

export interface AcquisitionTouch {
  version: 1;
  capturedAt: string;
  landingPath: string;
  referrerHost: string | null;
  source: TouchSource;
  sourceDetail: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
}

export const FIRST_KEY = "timo-first-touch";
export const LAST_KEY = "timo-last-touch";

export function captureTouch(): void {
  try {
    var q = new URLSearchParams(location.search);
    var host: string | null = null;
    try { host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : null; } catch (e) { host = null; }
    if (host === location.hostname.replace(/^www\./, "")) host = null; // moving between our own pages is not a source
    var utm = function (k: string) { var v = q.get("utm_" + k); return v ? v.slice(0, 80) : null; };
    var us = utm("source"), um = utm("medium");
    var source = "direct", detail: string | null = null;
    var AI: Record<string, string> = { "chatgpt.com": "chatgpt", "chat.openai.com": "chatgpt", "perplexity.ai": "perplexity", "claude.ai": "claude", "copilot.microsoft.com": "copilot", "gemini.google.com": "gemini", "you.com": "you", "phind.com": "phind" };
    var LISTING: Record<string, string> = { "producthunt.com": "producthunt", "g2.com": "g2", "capterra.com": "capterra", "alternativeto.net": "alternativeto", "chromewebstore.google.com": "chrome-web-store", "apps.apple.com": "app-store" };
    var SEARCH = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|search\.yahoo\.com|yahoo\.com|ecosia\.org|search\.brave\.com|yandex\.[a-z.]+|baidu\.com|startpage\.com|qwant\.com)$/;
    var match = function (map: Record<string, string>, h: string) { for (var k in map) { if (h === k || h.slice(-(k.length + 1)) === "." + k) return map[k]!; } return null; };
    // Explicit tags win over guessing from the referrer.
    if (us) {
      var s = us.toLowerCase();
      detail = s;
      if (um && /cpc|paid|ppc|display/.test(um.toLowerCase())) source = "other";
      else if (match(AI, s) || /^(chatgpt|perplexity|claude|copilot|gemini)$/.test(s)) source = "ai_answer";
      else if (um && um.toLowerCase() === "listing") source = "listing";
      else if (/^(google|bing|duckduckgo)$/.test(s) && um && um.toLowerCase() === "organic") source = "organic_search";
      else source = "referral";
    } else if (host) {
      var ai = match(AI, host), listing = match(LISTING, host), engine = host.match(SEARCH);
      if (ai) { source = "ai_answer"; detail = ai; }
      else if (listing) { source = "listing"; detail = listing; }
      else if (engine) { source = "organic_search"; detail = (engine[2] || host).split(".")[0] || host; }
      else { source = "referral"; detail = host; }
    }
    var touch = {
      version: 1, capturedAt: new Date().toISOString(), landingPath: location.pathname.slice(0, 120), referrerHost: host,
      source: source, sourceDetail: detail, utmSource: us, utmMedium: um, utmCampaign: utm("campaign"), utmContent: utm("content"),
    };
    var json = JSON.stringify(touch);
    if (!localStorage.getItem("timo-first-touch")) localStorage.setItem("timo-first-touch", json);
    // A plain reload or internal navigation should not replace a real last touch with "direct".
    if (source !== "direct" || !localStorage.getItem("timo-last-touch")) localStorage.setItem("timo-last-touch", json);
  } catch (e) { /* storage blocked: attribution is best-effort */ }
}

/** The inline <script> the static pages carry. */
export const touchScript = () => `<script>(${captureTouch.toString()})();</script>`;

export function readTouches(): { first: AcquisitionTouch | null; last: AcquisitionTouch | null } {
  const read = (k: string) => { try { const v = localStorage.getItem(k); return v ? (JSON.parse(v) as AcquisitionTouch) : null; } catch { return null; } };
  return { first: read(FIRST_KEY), last: read(LAST_KEY) };
}
